import { AuthState } from "@/types/auth";
import { create } from "zustand";
import { getParams } from "@/hooks";
import { toFriendlyErrorMessage } from "@/utils/api";
import { explorerPrefixedPath } from "@/utils";
import { getDefaultRouteAfterLogin } from "@/constants";
import { removeAuthParamsFromUrl } from "@/utils/location";
import { executeAPIRequest, getToken, setToken } from "@/utils/api";
import { stringify } from "qs";
import { useUserStore } from "@/store/user";
import { useSavedDeploymentConfigStore } from "@/store/savedDeploymentConfig";
import { initialiseSupport, shutdownSupport } from "@/utils/support";
import { setIsCreditsEnabled } from "@/utils/credits";
import env from "@/env";
import localCookies from "@/utils/cookie";
import { toStringWithLoginVersionSuffix } from "@/utils/user";
import { toast } from "@/components/ui/use-toast";
import {
  AuthUserProfile,
  decodeToken,
  exchangeCodeForSession,
  getAuthorizationUrl,
  getUser,
  logoutSession,
  mapWorkOSUserToProfile,
  patchUserMetadata,
  refreshSession,
  WorkOSAuthResponse,
  WorkOSEmailVerificationRequired
} from "@/utils/workos";
import { UserAuthToken, UserMetaData } from "@/types/user";

type AuthStoreState = AuthState & {
  bootstrap: () => Promise<void>;
  beginSso: (provider: string) => Promise<void>;
  completeOtpLogin: (token?: string, isNewUser?: boolean) => Promise<void>;
  refreshProfile: () => Promise<void>;
  logout: (returnTo?: string) => Promise<void>;
  updateMetadata: (meta: Partial<UserMetaData>) => Promise<AuthUserProfile<UserMetaData> | void>;
  setAccessToken: (token?: string) => void;
  clearError: () => void;
  // Email verification state
  emailVerificationRequired?: WorkOSEmailVerificationRequired | null;
  setEmailVerificationRequired: (data: WorkOSEmailVerificationRequired | null) => void;
  clearEmailVerificationRequired: () => void;
};

const INITIAL_STATE: AuthState = {
  status: "bootstrapping",
  isReady: false
};

const PERSISTED_STATE_KEY = toStringWithLoginVersionSuffix("authPersistedState");
const TOKEN_EXPIRY_SKEW_MS = 60 * 1000;

const persistState = (state: AuthState) => {
  try {
    localCookies.setItem(PERSISTED_STATE_KEY, JSON.stringify(state));
  } catch (e) {
    console.log(e);
  }
};

const isTokenUsable = (accessToken: string) => {
  const decoded = decodeToken<UserAuthToken>(accessToken);
  if (!decoded?.exp) {
    return true;
  }
  return decoded.exp * 1000 - Date.now() > TOKEN_EXPIRY_SKEW_MS;
};

const loadPersistedAuthState = (): AuthState | null => {
  try {
    const raw = localCookies.getItem(PERSISTED_STATE_KEY);
    if (!raw) {
      return null;
    }
    return JSON.parse(raw) as AuthState;
  } catch {
    return null;
  }
};

const getAuthUserIdFromToken = (token: string) => {
  const decoded = decodeToken<UserAuthToken>(token);
  return decoded?.sub;
};

const getUserIdFromToken = (token: string) => {
  const decoded = decodeToken<UserAuthToken>(token);
  return decoded?.["https://io.net/user"]?.io_id || decoded?.sub;
};

const fetchInternalUserId = async (userId?: string, currentInternalUserId?: string) => {
  if (!userId) {
    throw new Error("user id not found in auth profile");
  }
  if (currentInternalUserId) {
    return currentInternalUserId;
  }

  const response = await executeAPIRequest<{
    data: {
      user_uuid: string;
    };
    status: string;
  }>({
    method: "get",
    url: `/auth/user-uuid?${stringify({
      io_id: userId
    })}`
  });

  return response.data.user_uuid;
};

const enrichProfileWithWalletAddresses = async (
  profile: AuthUserProfile<UserMetaData>,
  internalUserId?: string
) => {
  // Skip fetching if wallet addresses already exist in metadata
  const hasSolanaAddresses = profile.user_metadata?.solana_wallet_addresses !== undefined;
  const hasAptosAddresses = profile.user_metadata?.aptos_wallet_addresses !== undefined;

  if (hasSolanaAddresses && hasAptosAddresses) {
    setIsCreditsEnabled(env.FEATUREFLAG_IO_CREDITS_ENABLED);
    return profile;
  }

  try {
    if (internalUserId) {
      useUserStore.getState().setUserId(internalUserId);
    }
    const addresses = await useUserStore.getState().fetchWalletAddresses();
    setIsCreditsEnabled(env.FEATUREFLAG_IO_CREDITS_ENABLED);

    return {
      ...profile,
      user_metadata: {
        ...profile.user_metadata,
        ...{
          solana_wallet_addresses: addresses.solana_addresses || [],
          aptos_wallet_addresses: addresses.aptos_addresses || []
        }
      }
    };
  } catch (e) {
    console.log(e);
  }

  return profile;
};

const useAuthStore = create<AuthStoreState>((set, get) => ({
  ...INITIAL_STATE,
  token: undefined,
  userId: undefined,
  authUserId: undefined,
  internalUserId: undefined,
  profile: undefined,
  error: undefined,
  ssoType: undefined,
  isReady: false,
  emailVerificationRequired: null,
  setEmailVerificationRequired: (data) => set({ emailVerificationRequired: data }),
  clearEmailVerificationRequired: () => set({ emailVerificationRequired: null }),
  setAccessToken: (token) => {
    setToken(token);
    set({ token });
  },
  clearError: () => {
    set({ error: undefined });
  },
  bootstrap: async () => {
    if (get().isReady) {
      return;
    }
    set({ status: "bootstrapping", error: undefined });

    const setMinimalAuthState = (response: WorkOSAuthResponse) => {
      const accessToken = response.accessToken;
      setToken(accessToken);
      const authUserId = getAuthUserIdFromToken(accessToken);
      const userId = getUserIdFromToken(accessToken);
      const profile = mapWorkOSUserToProfile<UserMetaData>(response.user);
      const nextState: AuthState = {
        status: "authenticated",
        isReady: true,
        token: accessToken,
        userId,
        authUserId,
        internalUserId: undefined,
        profile,
        error: undefined,
        ssoType: get().ssoType,
        isNewUser: response.is_new_user
      };
      persistState(nextState);
      set(nextState);
      useUserStore.getState().setUserId(userId);
    };

    const enrichAuthStateInBackground = async (response: WorkOSAuthResponse) => {
      const userId = getUserIdFromToken(response.accessToken);
      const profile = mapWorkOSUserToProfile<UserMetaData>(response.user);
      try {
        const internalUserId = await fetchInternalUserId(userId, get().internalUserId);
        const enrichedProfile = await enrichProfileWithWalletAddresses(profile, internalUserId);
        if (internalUserId) {
          useUserStore.getState().setUserId(internalUserId);
        }
        initialiseSupport({
          userId: internalUserId,
          name: enrichedProfile?.name,
          email: enrichedProfile?.email,
          createdAt: enrichedProfile?.created_at
            ? Date.parse(enrichedProfile?.created_at)
            : undefined
        });
        set({
          internalUserId,
          profile: enrichedProfile
        });
      } catch (e) {
        console.error("Auth enrichment failed", e);
      }
    };

    try {
      const params = getParams<{
        error?: string;
        error_description?: string;
        code?: string;
        state?: string;
      }>();

      if (params.error_description) {
        throw new Error(toFriendlyErrorMessage(params.error_description));
      }

      if (params.code && params.state) {
        try {
          const response = await exchangeCodeForSession(params.code, params.state);

          // Check if email verification is required
          if (
            "requiresEmailVerification" in response &&
            response.requiresEmailVerification === true
          ) {
            // Store email verification data and show OTP screen
            removeAuthParamsFromUrl();
            set({
              emailVerificationRequired: response as WorkOSEmailVerificationRequired,
              status: "unauthenticated",
              isReady: true,
              error: undefined
            });
            return;
          }

          // Normal authentication flow
          const authResponse = response as WorkOSAuthResponse;
          removeAuthParamsFromUrl();
          setMinimalAuthState(authResponse);
          void enrichAuthStateInBackground(authResponse);
          return;
        } catch (e) {
          console.error("WorkOS exchange code for session failed", e);
          removeAuthParamsFromUrl();
          let message =
            (e as Error).message ||
            "Unable to login. Please contact support for further assistance.";
          if (message.includes("blocked")) {
            message = "Unable to login. Please contact support for further assistance.";
          }
          toast({
            title: "Authentication failed",
            variant: "error",
            description: message
          });
          setToken(undefined);
          set({
            status: "unauthenticated",
            isReady: true,
            token: undefined,
            userId: undefined,
            authUserId: undefined,
            internalUserId: undefined,
            profile: undefined,
            error: message,
            isNewUser: undefined,
            emailVerificationRequired: null
          });
          const loginUrl = `${explorerPrefixedPath("/login")}${message ? `?error=${encodeURIComponent(message)}` : ""}`;
          window.location.replace(loginUrl);
          return;
        }
      }

      const persisted = loadPersistedAuthState();
      if (
        persisted?.token &&
        persisted.status === "authenticated" &&
        isTokenUsable(persisted.token)
      ) {
        setToken(persisted.token);
        set({
          ...persisted,
          status: "authenticated",
          isReady: true,
          error: undefined
        });
        const effectiveUserId = persisted.internalUserId ?? persisted.userId;
        if (effectiveUserId) {
          useUserStore.getState().setUserId(effectiveUserId);
        }

        void (async () => {
          try {
            const refreshResponse = await refreshSession();
            setMinimalAuthState(refreshResponse);
            void enrichAuthStateInBackground(refreshResponse);
          } catch (e) {
            console.error("Background session refresh failed", e);
            setToken(undefined);
            localCookies.removeItem(PERSISTED_STATE_KEY);
            useUserStore.getState().setUserId(undefined);
            set({
              status: "unauthenticated",
              isReady: true,
              token: undefined,
              userId: undefined,
              authUserId: undefined,
              internalUserId: undefined,
              profile: undefined,
              error: undefined,
              isNewUser: undefined,
              emailVerificationRequired: null
            });
          }
        })();
        return;
      }

      try {
        const refreshResponse = await refreshSession();
        setMinimalAuthState(refreshResponse);
        void enrichAuthStateInBackground(refreshResponse);
      } catch (e) {
        const urlError = typeof params.error === "string" ? params.error : undefined;
        setToken(undefined);
        localCookies.removeItem(PERSISTED_STATE_KEY);
        useUserStore.getState().setUserId(undefined);
        set({
          status: "unauthenticated",
          isReady: true,
          token: undefined,
          userId: undefined,
          authUserId: undefined,
          internalUserId: undefined,
          profile: undefined,
          error: urlError ?? undefined,
          isNewUser: undefined,
          emailVerificationRequired: null
        });
        if (urlError) {
          removeAuthParamsFromUrl();
          toast({
            title: "Authentication failed",
            variant: "error",
            description: urlError
          });
        }
      }
    } catch (e) {
      setToken(undefined);
      localCookies.removeItem(PERSISTED_STATE_KEY);
      useUserStore.getState().setUserId(undefined);
      set({
        status: "unauthenticated",
        isReady: true,
        token: undefined,
        userId: undefined,
        authUserId: undefined,
        internalUserId: undefined,
        profile: undefined,
        error: (e as Error).message,
        isNewUser: undefined,
        emailVerificationRequired: null
      });
    }
  },
  beginSso: async (provider) => {
    set({ ssoType: provider, error: undefined });
    try {
      const url = await getAuthorizationUrl(provider);
      window.location.assign(url);
    } catch (e) {
      const errorMessage = (e as Error).message;
      set({ error: errorMessage, status: "unauthenticated", isReady: true });
      toast({
        title: "Sign-in failed",
        variant: "error",
        description: errorMessage
      });
      throw e;
    }
  },
  completeOtpLogin: async (token, isNewUser) => {
    if (!token) {
      set({ error: "token not found", status: "unauthenticated", isReady: true });
      return;
    }
    const authUserId = getAuthUserIdFromToken(token);
    if (!authUserId) {
      set({ error: "user id not found", status: "unauthenticated", isReady: true });
      return;
    }

    try {
      const profile = await getUser<UserMetaData>(token, authUserId);
      const userId = getUserIdFromToken(token);
      setToken(token);
      set({
        status: "authenticated",
        isReady: true,
        token,
        userId,
        authUserId,
        internalUserId: undefined,
        profile,
        error: undefined,
        isNewUser
      });
      useUserStore.getState().setRequiresLoginModalOpen(false);
      void (async () => {
        try {
          const internalUserId = await fetchInternalUserId(userId, get().internalUserId);
          const enrichedProfile = await enrichProfileWithWalletAddresses(profile, internalUserId);
          if (internalUserId) {
            useUserStore.getState().setUserId(internalUserId);
          }
          initialiseSupport({
            userId: internalUserId,
            name: enrichedProfile?.name,
            email: enrichedProfile?.email,
            createdAt: enrichedProfile?.created_at
              ? Date.parse(enrichedProfile?.created_at)
              : undefined
          });
          set({ internalUserId, profile: enrichedProfile });
        } catch (e) {
          console.error("Auth enrichment failed", e);
        }
      })();
    } catch (e) {
      set({ status: "unauthenticated", isReady: true, error: (e as Error).message });
    }
  },
  updateMetadata: async (meta) => {
    const { token, authUserId } = get();
    if (!token || !authUserId) {
      throw new Error("profile not found");
    }
    await patchUserMetadata<UserMetaData>(token, authUserId, meta);
    const refreshedProfile = await getUser<UserMetaData>(token, authUserId);
    const updatedProfile = await enrichProfileWithWalletAddresses(
      refreshedProfile,
      get().internalUserId
    );
    set({ profile: updatedProfile });
    return updatedProfile;
  },
  refreshProfile: async () => {
    const { token, authUserId } = get();
    if (!token || !authUserId) {
      return;
    }
    const refreshedProfile = await getUser<UserMetaData>(token, authUserId);
    const updatedProfile = await enrichProfileWithWalletAddresses(
      refreshedProfile,
      get().internalUserId
    );
    set({ profile: updatedProfile });
  },
  logout: async (returnTo) => {
    shutdownSupport();
    const userId = useUserStore.getState().userId;
    if (userId) {
      useSavedDeploymentConfigStore.getState().clearForUser(userId);
    }
    useUserStore.getState().setUserId(undefined);

    const redirectTarget = returnTo ?? getDefaultRouteAfterLogin();
    await logoutSession(getToken(), redirectTarget);

    localCookies.removeItem(PERSISTED_STATE_KEY);
    localCookies.removeItem("LOGIN_REDIRECT_ROUTE");
    localCookies.removeItem("LOGIN_TRACKED");

    setToken(undefined);
    set({
      status: "unauthenticated",
      isReady: true,
      token: undefined,
      userId: undefined,
      authUserId: undefined,
      internalUserId: undefined,
      profile: undefined,
      error: undefined,
      isNewUser: undefined
    });

    window.location.assign("/");
  }
}));

export const useAuthState = <T>(selector: (state: AuthStoreState) => T) => {
  return useAuthStore(selector);
};

export const useAuthActions = () => {
  return useAuthStore((state) => ({
    bootstrap: state.bootstrap,
    setEmailVerificationRequired: state.setEmailVerificationRequired,
    clearEmailVerificationRequired: state.clearEmailVerificationRequired,
    beginSso: state.beginSso,
    completeOtpLogin: state.completeOtpLogin,
    refreshProfile: state.refreshProfile,
    logout: state.logout,
    updateMetadata: state.updateMetadata,
    setAccessToken: state.setAccessToken,
    clearError: state.clearError
  }));
};

export const AuthMachineContext = {
  useSelector: <T>(selector: (state: { value: string; context: Record<string, unknown> }) => T) => {
    return useAuthStore((state) => {
      const context = {
        isLoggedIn: state.status === "authenticated",
        token: state.token,
        userId: state.userId,
        authUserId: state.authUserId,
        internalUserId: state.internalUserId,
        profile: state.profile,
        failedError: state.error,
        ssoType: state.ssoType
      };
      return selector({ value: state.status, context });
    });
  }
};

export const getAuthContext = () => {
  const state = useAuthStore.getState();
  return {
    ...state,
    isLoggedIn: state.status === "authenticated"
  };
};

export const getAuthActions = () => {
  const state = useAuthStore.getState();
  return {
    bootstrap: state.bootstrap,
    beginSso: state.beginSso,
    completeOtpLogin: state.completeOtpLogin,
    refreshProfile: state.refreshProfile,
    logout: state.logout,
    updateMetadata: state.updateMetadata,
    setAccessToken: state.setAccessToken,
    clearError: state.clearError
  };
};
